import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import State from "../viewer2d/state";
import { ReactSVGPanZoom, setPointOnViewerCenter } from "react-svg-pan-zoom";
import { Catalog } from "../../catalog/catalog";
import { provideAreaOfPolygon } from "../../utils/geometry";

import { List } from "immutable";

// Fixed capture frame — avoids full-browser width so PDF is not mostly white
// with the plan in one corner and the legend in the opposite corner.
const VIEWER_W = 1100;
const VIEWER_H = 640;

const floorAreaLabel = (areas, layer) => {
  const m2 = areas
    .toArray()
    .reduce(
      (accumulator, area) =>
        parseFloat(accumulator) + parseFloat(provideAreaOfPolygon(area, layer)),
      0.0
    );
  const m2Text = Number(m2).toFixed(2);
  const ft2Text = (Number(m2) * 10.764).toFixed(2);
  return `${m2Text}m${String.fromCharCode(0xb2)}( ${ft2Text}ft${String.fromCharCode(
    0xb2
  )}) approx.`;
};

const legendBoxStyle = {
  width: "fit-content",
  maxWidth: "100%",
  height: "auto",
  overflow: "visible",
  alignSelf: "flex-end",
  margin: "8px 0 0 0",
  border: "1px solid",
  backgroundColor: "white",
  padding: "10px 12px",
};

const Legend = ({ safetysymbols }) => (
  <div style={legendBoxStyle}>
    <p style={{ textAlign: "left", fontSize: "13px", margin: "1px" }}>Legend</p>
    <hr />
    <div style={{ display: "flex", flexWrap: "wrap" }}>
      {safetysymbols.valueSeq().map((symbol) => (
        <div
          key={symbol.type}
          style={{
            width: "60px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            rowGap: "3px",
          }}
        >
          <img
            src={require(`../../assets/firesafety/${symbol.type.replace(
              "firesafety-",
              ""
            )}/legend.png`)}
            alt=""
            style={{ width: "50%" }}
          />
          <p
            style={{
              margin: 0,
              fontSize: "11px",
              paddingLeft: "5px",
            }}
          >
            {symbol.name}
          </p>
        </div>
      ))}
    </div>
  </div>
);

const ViewerFinish = ({ state, width, height, catalog, componentRef }) => {
  let defaultState = {
    a: 0.5,
    b: 0,
    SVGWidth: 30000,
    c: 0,
    mode: "idle",
    d: 0.5,
    e: -6814,
    f: -3505,
    miniatureOpen: true,
    SVGHeight: 20000,
    pinchPointDistance: null,
    scaleFactorMax: 5,
    lastAction: null,
    viewerWidth: VIEWER_W,
    startX: null,
    startY: null,
    version: 2,
    focus: false,
    scaleFactorMin: 0.05,
    viewerHeight: VIEWER_H,
    prePinchMode: null,
    endX: null,
    endY: null,
  };
  const [viewerState, setViewerState] = useState(defaultState);
  let onChangeValue = (value) => {
    // Keep viewer size locked to capture frame (don't let pan-zoom drift sizes).
    setViewerState({
      ...value,
      viewerWidth: VIEWER_W,
      viewerHeight: VIEWER_H,
      scaleFactorMin: 0.05,
    });
  };
  let layers = state.getIn(["scene", "layers"]);
  let selectedLayer = state.getIn(["scene", "selectedLayer"]);
  let layer = state.getIn(["scene", "layers", selectedLayer]);
  let areas = state.getIn(["scene", "layers", selectedLayer, "areas"]);

  // Multi-floor PDF: when the selected floor changes, pan/zoom so that floor's
  // walls fill the fixed viewer. Wrong viewerWidth caused plans to sit in the
  // corner and upper floors to capture as blank white pages.
  useEffect(() => {
    if (!layer) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let count = 0;
    const vertices = layer.get("vertices");
    if (vertices) {
      vertices.forEach((v) => {
        if (!v || v.x == null || v.y == null) return;
        minX = Math.min(minX, v.x);
        minY = Math.min(minY, v.y);
        maxX = Math.max(maxX, v.x);
        maxY = Math.max(maxY, v.y);
        count += 1;
      });
    }
    if (!count || !Number.isFinite(minX)) return;

    const sceneHeight = state.getIn(["scene", "height"]) || 20000;
    const cx = (minX + maxX) / 2;
    // SVG Y is flipped vs scene Y (translate + scale(-1) in State).
    const cySvg = sceneHeight - (minY + maxY) / 2;
    const spanX = Math.max(maxX - minX, 500);
    const spanY = Math.max(maxY - minY, 500);
    const pad = 1.35;
    const zoom = Math.max(
      0.08,
      Math.min(VIEWER_W / (spanX * pad), VIEWER_H / (spanY * pad), 3)
    );

    setViewerState((prev) => {
      const base = {
        ...prev,
        viewerWidth: VIEWER_W,
        viewerHeight: VIEWER_H,
        scaleFactorMin: 0.05,
      };
      try {
        return setPointOnViewerCenter(base, cx, cySvg, zoom);
      } catch (err) {
        return base;
      }
    });
  }, [selectedLayer, layer, state]);

  let safetysymbols = layer
    .getIn(["items"])
    .filter((item) => item.type.includes("firesafety"));
  safetysymbols = safetysymbols.size
    ? safetysymbols.reduce((acc, current) => {
        if (!acc.some((item) => item.type === current.type)) {
          acc = acc.push(current);
        }
        return acc;
      }, new List())
    : safetysymbols;
  let evacuationPlan = state.get("evacuationPlan");

  const floorHeader = (
    <div style={{ textAlign: "center", padding: "6px 0" }}>
      <p style={{ margin: 0 }}>{layer.get("visibleName").toUpperCase()}</p>
      <p style={{ margin: 0 }}>{floorAreaLabel(areas, layer)}</p>
    </div>
  );

  const planViewer = (
    <ReactSVGPanZoom
      key={`finish-viewer-${selectedLayer}`}
      value={viewerState}
      onChangeValue={onChangeValue}
      tool="auto"
      preventPanOutside={true}
      disableDoubleClickZoomWithToolAuto={true}
      detectAutoPan={false}
      scaleFactorMax={5}
      scaleFactorMin={0.05}
      miniaturePosition="none"
      toolbarPosition="none"
      width={VIEWER_W}
      height={VIEWER_H}
    >
      <svg width={VIEWER_W} height={VIEWER_H}>
        <g>
          <State state={state} catalog={catalog} showGuides={false} />
        </g>
      </svg>
    </ReactSVGPanZoom>
  );

  const planColumn = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: VIEWER_W,
        background: "#ffffff",
      }}
    >
      {floorHeader}
      {planViewer}
      <Legend safetysymbols={safetysymbols} />
    </div>
  );

  return (
    <Fragment>
      <div
        ref={componentRef}
        width={width}
        height={height}
        data-bs-floorId={`floorplan-${selectedLayer}-${Date.now().toString()}`}
        style={{
          display: "inline-block",
          width: "fit-content",
          maxWidth: "100%",
          overflow: "hidden",
          background: "#ffffff",
        }}
      >
        {evacuationPlan ? (
          <div style={{ display: "flex", width: "fit-content" }}>
            <div
              style={{
                width: 220,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ background: "#08b763", color: "white" }}>
                <p
                  style={{
                    fontSize: "18px",
                    fontWeight: "800",
                    margin: "0",
                    color: "white",
                    textAlign: "center",
                    padding: 8,
                  }}
                >
                  FIRE EVACUATION PLAN
                </p>
              </div>
              <div style={{ background: "#2196f3", color: "white", flex: 1 }}>
                <p style={{ padding: 10, margin: 0, fontSize: "12px" }}>
                  <span>
                    <u>
                      <b>IN CASE OF FIRE</b>
                    </u>
                  </span>{" "}
                  <br />
                  <br />
                  Immediately raise the alarm by operating the nearest RED break
                  glass call point and:
                  <ul style={{ paddingLeft: 20 }}>
                    <li>
                      Dial 999 to confirm the location and details of the fire
                    </li>
                    <li>
                      Close all doors and windows in the immediate vicinity{" "}
                    </li>
                    <li>
                      Commence evacuation using the nearest, safest evacuation
                      route.
                    </li>
                  </ul>
                </p>
              </div>
              <div style={{ background: "#08b763", color: "white", flex: 1 }}>
                <p style={{ padding: 10, margin: 0, fontSize: "12px" }}>
                  <br />
                  <p style={{ margin: 0 }}>
                    <b>ON HEARING THE FIRE ALARM</b>
                  </p>
                  <br />
                  <span>
                    <u>
                      <b>A Continuous Sound Means</b>
                    </u>
                  </span>
                  <br />
                  <br />
                  The alarm has been activated in your area. Investigate your
                  area and evacuate as necessary.
                  <br />
                  <br />
                  <span>
                    <u>
                      <b>An Intermittent Sound Means</b>
                    </u>
                  </span>
                  <br />
                  <br />
                  The alarm was activated in the adjacent area. Check your area
                  for signs of a fire. Identify the location of the alarm zone
                  and prepare your area as necessary in case of emergency
                  evacuation.
                </p>
              </div>
              <div
                style={{
                  textAlign: "center",
                  background: "#da2727",
                  color: "white",
                  flex: 1,
                }}
              >
                <p style={{ padding: 10, margin: 0, fontSize: "12px" }}>
                  <br />
                  <p style={{ margin: 0 }}>
                    <b>EMERGENCY NUMBER</b>
                  </p>
                  <br />
                  <p style={{ margin: 0, fontSize: 24 }}>
                    <b>999</b>
                  </p>
                </p>
              </div>
            </div>
            {planColumn}
          </div>
        ) : (
          planColumn
        )}
      </div>
    </Fragment>
  );
};
ViewerFinish.propTypes = {
  catalog: PropTypes.instanceOf(Catalog),
};
export default ViewerFinish;
